import { useContext, useEffect, useState } from 'react'
import { v4 as uuid } from 'uuid'
import { useNavigate } from 'react-router-dom'

import posthogEvents from '../posthog/eventNames.json'
import {
  SendBrandEngagementEvent,
  SendBrandMarketingToolClickedEvent,
  SendCaptureClicked,
  SendExperienceFetchedEvent,
  SendPermissionAnsweredEvent,
  SendProductIdentifiedEvent,
  SendResultDisplayedEvent,
  SendScanAgainClickedEvent,
  SendSocialClickedEvent,
  SendStartSessionEvent,
  SendContactUsClickedEvent,
} from './types'
import { PosthogContext } from '../posthog/PosthogContext'

const usePosthogEvent = (prevPath?: string) => {
  const { posthog } = useContext(PosthogContext)
  const navigate = useNavigate()
  const [cookiesEnabled, setCookiesEnabled] = useState(true)

  const getUniqueId = () => {
    return cookiesEnabled && localStorage.getItem('uniqueId')
  }

  const setUniqueId = (uniqueId: string) => {
    cookiesEnabled && localStorage.setItem('uniqueId', uniqueId)
  }

  const eventUserIdentify = () => {
    const uniqueId = getUniqueId()
    if (uniqueId) {
      posthog?.identify(uniqueId)
    } else {
      const newUniqueId = uuid()
      setUniqueId(newUniqueId)
      posthog?.identify(newUniqueId)
    }
  }

  const sendPostHogEvent = (eventName: string, props?: Record<string, any>) => {
    let urlProps: Record<string, string> = {};
    
    try {
      // Safely get the current browser URL
      const currentUrl = typeof window !== 'undefined' && window.location ? window.location.href : '';
      
      if (currentUrl) {
        // Parse URL query parameters
        const urlParams = new URLSearchParams(new URL(currentUrl).search);
        
        // Convert URLSearchParams to an object
        urlParams.forEach((value, key) => {
          urlProps[key] = value;
        });
      }
    } catch (error) {
      console.error('Error parsing URL for PostHog event:', error);
      // Continue with empty urlProps if parsing fails
      urlProps = {};
    }
  
    // Get appSessionId from sessionStorage, with fallback if sessionStorage is unavailable
    const appSessionId = sessionStorage?.getItem('appSessionId') || 'unknown';
  
    // Merge all properties and send the event
    posthog?.capture(eventName, {
      ...urlProps,              // Spread URL parameters (if any)
      ...props,                 // Spread any additional props passed to the function
      appSessionId,             // Add appSessionId
    });
  };

  const sendProductIdentifiedEvent: SendProductIdentifiedEvent = (props) => {
    posthog?.capture(posthogEvents.productIdentified, props)
  }

  const sendExperienceFetchedEvent: SendExperienceFetchedEvent = (props) => {
    posthog?.capture(posthogEvents.experienceFetched, props)
  }

  const sendSessionStartedEvent: SendStartSessionEvent = (props) => {
    posthog?.capture(posthogEvents.sessionStarted, props)
  }

  const sendCaptureClickedEvent: SendCaptureClicked = (props) => {
    posthog?.capture(posthogEvents.captureBtnClicked, props)
  }

  const sendResultDisplayedEvent: SendResultDisplayedEvent = (props) => {
    posthog?.capture(posthogEvents.resultDisplayed, props)
  }

  const sendScanAgainClickedEvent: SendScanAgainClickedEvent = (props) => {
    posthog?.capture(posthogEvents.scanAgainBtnClicked, props)
  }

  const sendHomeClickedEvent: SendScanAgainClickedEvent = (props) => {
    posthog?.capture(posthogEvents.homeBtnClicked, props)
  }

  const sendBrandEngagementClickedEvent: SendBrandEngagementEvent = (props) => {
    posthog?.capture(posthogEvents.brandEngagementBtnClicked, props)
  }

  const sendBrandMarketingToolClickedEvent: SendBrandMarketingToolClickedEvent = (props) => {
    posthog?.capture(posthogEvents.brandMarketingToolClicked, props)
  }

  const sendSocialClickedEvent: SendSocialClickedEvent = (props) => {
    posthog?.capture(posthogEvents.socialClicked, props)
  }

  const sendContactUsClickedEvent: SendContactUsClickedEvent = (props) => {
    posthog?.capture(posthogEvents.socialClicked, props)
  }

  const sendPermissionAnsweredEvent: SendPermissionAnsweredEvent = (props) => {
    posthog?.capture(posthogEvents.permissionAnswered, props)
  }

  useEffect(() => {
    const checkCookiesEnabled = () => {
      try {
        localStorage.setItem('test', 'test')
        localStorage.removeItem('test')
        setCookiesEnabled(true)
      } catch (error) {
        setCookiesEnabled(false)
        navigate('/error/permission-required', {
          state: { isCookiesPermissionError: true, prevPath },
        })
      }
    }

    checkCookiesEnabled()
  }, [])

  useEffect(() => {
    posthog && cookiesEnabled && eventUserIdentify()
  }, [posthog, cookiesEnabled])

  return {
    sendPostHogEvent,
    sendProductIdentifiedEvent,
    sendExperienceFetchedEvent,
    sendSessionStartedEvent,
    sendCaptureClickedEvent,
    sendResultDisplayedEvent,
    sendScanAgainClickedEvent,
    sendHomeClickedEvent,
    sendBrandEngagementClickedEvent,
    sendBrandMarketingToolClickedEvent,
    sendSocialClickedEvent,
    sendPermissionAnsweredEvent,
    sendContactUsClickedEvent,
  }
}

export default usePosthogEvent